/**
 * Messages Selectors
 *
 * Memoized selectors for accessing chat messages state.
 * Using createSelector ensures components only re-render when selected data changes.
 */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';

// ============================================
// Base Selectors
// ============================================

const selectMessagesState = (state: RootState) => state.chat.messages;

// ============================================
// Message Selectors (Memoized)
// ============================================

export const selectMessages = createSelector(
  [selectMessagesState],
  (messages) => messages.messages,
);

export const selectIsLoading = createSelector(
  [selectMessagesState],
  (messages) => messages.isLoading,
);

export const selectChatError = createSelector([selectMessagesState], (messages) => messages.error);

export const selectCurrentStreamingMessageId = createSelector(
  [selectMessagesState],
  (messages) => messages.currentStreamingMessageId,
);

export const selectHasMore = createSelector([selectMessagesState], (messages) => messages.hasMore);

export const selectNextCursor = createSelector(
  [selectMessagesState],
  (messages) => messages.nextCursor,
);

export const selectIsLoadingMore = createSelector(
  [selectMessagesState],
  (messages) => messages.isLoadingMore,
);

export const selectSkipHistoryLoad = createSelector(
  [selectMessagesState],
  (messages) => messages.skipHistoryLoad,
);

export const selectThinkingMessages = createSelector(
  [selectMessagesState],
  (messages) => messages.thinkingMessages,
);

export const selectPendingQuery = createSelector(
  [selectMessagesState],
  (messages) => messages.pendingQuery,
);

export const selectLatestLeadingQuestions = createSelector([selectMessagesState], (messages) => {
  const allMessages = messages.messages;
  for (let i = allMessages.length - 1; i >= 0; i--) {
    const msg = allMessages[i];
    if (msg?.role === 'assistant' && Array.isArray(msg.content)) {
      return msg.content.filter((item) => item.type === 'leading-question');
    }
  }
  return [];
});
